import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, type ApiError, type UsageMetersResult } from '@whinerp/client';
import { Icon, Num } from '@whinerp/ui';
import { formatCount } from '@whinerp/contracts/format';
import { useFormat, useT } from '../app/i18n.js';

/**
 * The banner that appears before a limit stops somebody (F-143).
 *
 * The feature's own note: the warning appears IN-APP at 95%, not only by
 * email. An email nobody opened is not a warning, and the first time somebody
 * learns about a limit must not be the moment it refuses them.
 *
 * Only critical and exceeded. A banner at 80% on every page is a banner people
 * learn to ignore, and then the one that matters is invisible too.
 */
export function UsageBanner(): JSX.Element | null {
  const t = useT();
  const format = useFormat();

  const usage = useQuery<UsageMetersResult, ApiError>({
    queryKey: ['usage'],
    queryFn: () => api.usageMeters({}),
    // Slow: a meter does not move in a second, and this is on every screen.
    refetchInterval: 300_000,
  });

  const pressing = (usage.data?.meters ?? []).filter(
    (meter) => meter.state === 'critical' || meter.state === 'exceeded',
  );
  if (pressing.length === 0) return null;

  const worst = pressing.find((meter) => meter.state === 'exceeded') ?? pressing[0]!;

  return (
    <div
      data-component="UsageBanner"
      data-usage-banner={worst.state}
      role="status"
      className="flex flex-wrap items-center gap-2 border-b border-b-rule bg-warning-tint px-gutter py-1.5"
    >
      <Icon name="Warning" size="inline" decorative className="text-warning-text" />
      <span className="text-dense text-ink">
        {worst.state === 'exceeded' ? t('usage.exceeded') : t('usage.critical')}
      </span>
      {/* The meter and the number, never a bare warning: a person who cannot
          tell WHICH limit cannot act on it. */}
      <Num data-usage-banner-meter={worst.key} className="text-dense text-ink-600">
        {worst.label}
        {': '}
        {t('usage.of', {
          values: {
            used: formatCount(worst.used, format),
            limit: formatCount(worst.limit ?? 0, format),
          },
        })}
      </Num>
      <Link
        to="/setup/plan"
        data-usage-banner-link
        className="text-dense text-bronze-text underline decoration-bronze-rule underline-offset-4"
      >
        {t('usage.seeePlans')}
      </Link>
    </div>
  );
}
